import { motion, useScroll, useTransform } from 'framer-motion';
import { useRef } from 'react';

const LeveragePyramid = () => {
  const sectionRef = useRef<HTMLDivElement>(null);

  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ['start end', 'end start'],
  });

  // DESKTOP ONLY - Parallax background
  const bgY = useTransform(scrollYProgress, [0, 1], ['-8%', '8%']);
  const ambientGlowOpacity = useTransform(scrollYProgress, [0, 0.3, 0.8, 1], [0.05, 0.15, 0.15, 0.05]);

  // DESKTOP ONLY - Title and subtitle
  const titleOpacity = useTransform(scrollYProgress, [0.1, 0.2], [0, 1]);
  const titleY = useTransform(scrollYProgress, [0.1, 0.2], [30, 0]);

  // DESKTOP ONLY - Progressive layer reveals - bottom to top
  const layer1Opacity = useTransform(scrollYProgress, [0.25, 0.35], [0, 1]);
  const layer1Y = useTransform(scrollYProgress, [0.25, 0.35], [60, 0]);
  const layer1Scale = useTransform(scrollYProgress, [0.25, 0.35], [0.9, 1]);

  const layer2Opacity = useTransform(scrollYProgress, [0.35, 0.45], [0, 1]);
  const layer2Y = useTransform(scrollYProgress, [0.35, 0.45], [60, 0]);
  const layer2Scale = useTransform(scrollYProgress, [0.35, 0.45], [0.9, 1]);

  const layer3Opacity = useTransform(scrollYProgress, [0.45, 0.55], [0, 1]);
  const layer3Y = useTransform(scrollYProgress, [0.45, 0.55], [60, 0]);
  const layer3Scale = useTransform(scrollYProgress, [0.45, 0.55], [0.9, 1]);

  const layer4Opacity = useTransform(scrollYProgress, [0.55, 0.65], [0, 1]);
  const layer4Y = useTransform(scrollYProgress, [0.55, 0.65], [60, 0]);
  const layer4Scale = useTransform(scrollYProgress, [0.55, 0.65], [0.9, 1]);

  const layer5Opacity = useTransform(scrollYProgress, [0.65, 0.75], [0, 1]);
  const layer5Y = useTransform(scrollYProgress, [0.65, 0.75], [60, 0]);
  const layer5Scale = useTransform(scrollYProgress, [0.65, 0.75], [0.9, 1]);

  // DESKTOP ONLY - Arrow and label emphasis
  const arrowOpacity = useTransform(scrollYProgress, [0.75, 0.85], [0, 1]);
  const arrowX = useTransform(scrollYProgress, [0.75, 0.85], [-20, 0]);

  // DESKTOP ONLY - Level indicators
  const levelOpacity = useTransform(scrollYProgress, [0.7, 0.8], [0, 1]);

  const layers = [
    { label: 'Name', width: '95%', color: '#00AAFF', delay: 0.25, opacity: layer1Opacity, y: layer1Y, scale: layer1Scale, level: 1 },
    { label: 'Positioning', width: '80%', color: '#0099FF', delay: 0.35, opacity: layer2Opacity, y: layer2Y, scale: layer2Scale, level: 2 },
    { label: 'Pricing', width: '65%', color: '#0088FF', delay: 0.45, opacity: layer3Opacity, y: layer3Y, scale: layer3Scale, level: 3 },
    { label: 'Distribution', width: '50%', color: '#0077FF', delay: 0.55, opacity: layer4Opacity, y: layer4Y, scale: layer4Scale, level: 4 },
    { label: 'Platform', width: '35%', color: '#0066FF', delay: 0.65, opacity: layer5Opacity, y: layer5Y, scale: layer5Scale, level: 5 },
  ];

  return (
    <section ref={sectionRef} className="relative bg-dark-bg py-32 md:py-40 lg:py-48 overflow-hidden">
      {/* DESKTOP - Parallax background */}
      <motion.div style={{ y: bgY }} className="hidden lg:block absolute inset-0 pointer-events-none">
        <motion.div
          style={{ opacity: ambientGlowOpacity }}
          className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] md:w-[900px] h-[400px] md:h-[600px] rounded-full bg-accent-blue/5 blur-[140px] md:blur-[200px]"
        />
      </motion.div>

      {/* MOBILE - Static background */}
      <div className="lg:hidden absolute inset-0 pointer-events-none">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full bg-accent-blue/5 blur-[140px] opacity-60" />
      </div>

      <div className="max-w-[1200px] mx-auto px-6 md:px-8 relative z-10">
        {/* DESKTOP - Scroll-animated title */}
        <motion.h2
          style={{ opacity: titleOpacity, y: titleY }}
          className="hidden lg:block text-4xl md:text-5xl lg:text-6xl font-bold text-white text-center mb-8 md:mb-10 lg:mb-12"
        >
          Bottleneck Impact Hierarchy
        </motion.h2>

        <motion.p
          style={{ opacity: titleOpacity, y: titleY }}
          className="hidden lg:block text-lg md:text-xl lg:text-2xl text-gray-400 text-center mb-16 md:mb-20 lg:mb-24 max-w-3xl mx-auto leading-relaxed"
        >
          Impact increases as you move up. Removing higher bottlenecks unlocks everything below.
        </motion.p>

        {/* MOBILE - Simple fade-in title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5 }}
          className="lg:hidden text-[34px] font-bold text-white text-center mb-8"
        >
          Bottleneck Impact Hierarchy
        </motion.h2>

        <motion.p
          initial={{ opacity: 0, y: 15 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-50px" }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="lg:hidden text-[17px] leading-[1.7] text-gray-400 text-center mb-10 max-w-3xl mx-auto"
        >
          Impact increases as you move up. Removing higher bottlenecks unlocks everything below.
        </motion.p>

        <div className="relative max-w-[800px] mx-auto">
          {/* DESKTOP - Scroll-animated layers */}
          <div className="hidden lg:flex flex-col items-center gap-3 md:gap-4 lg:gap-5">
            {layers.map((layer) => (
              <motion.div
                key={layer.label}
                style={{ 
                  opacity: layer.opacity, 
                  y: layer.y, 
                  scale: layer.scale,
                  width: layer.width 
                }}
                className="relative group cursor-pointer"
              >
                <motion.div
                  whileHover={{ scale: 1.05, transition: { duration: 0.3 } }}
                  className="relative bg-dark-card border-2 rounded-lg md:rounded-xl overflow-hidden"
                  style={{ borderColor: layer.color }}
                >
                  {/* Background glow */}
                  <div
                    className="absolute inset-0 opacity-10 group-hover:opacity-20 transition-opacity duration-300"
                    style={{ backgroundColor: layer.color }}
                  />

                  {/* Content */}
                  <div className="relative py-6 md:py-8 lg:py-10 px-6 md:px-8">
                    <h3
                      className="text-xl md:text-2xl lg:text-3xl font-bold text-center"
                      style={{ color: layer.color }}
                    >
                      {layer.label}
                    </h3>
                  </div>

                  {/* Shine effect on hover */}
                  <motion.div
                    className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000"
                  />
                </motion.div>

                {/* Level indicator */}
                <motion.div
                  style={{ opacity: levelOpacity }}
                  className="absolute -right-16 md:-right-20 top-1/2 -translate-y-1/2 text-xs md:text-sm text-gray-500 font-medium hidden sm:block"
                >
                  Level {layer.level}
                </motion.div>
              </motion.div>
            ))}
          </div>

          {/* MOBILE - Simple stacked layers with fade-in */}
          <div className="lg:hidden flex flex-col items-center gap-3">
            {layers.map((layer, index) => (
              <motion.div
                key={layer.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-30px" }}
                transition={{ duration: 0.4, delay: index * 0.08 }}
                style={{ width: layer.width }}
                className="relative group cursor-pointer"
              >
                <div
                  className="relative bg-dark-card border-2 rounded-lg overflow-hidden"
                  style={{ borderColor: layer.color }}
                >
                  {/* Background glow */}
                  <div
                    className="absolute inset-0 opacity-10"
                    style={{ backgroundColor: layer.color }}
                  />

                  {/* Content */}
                  <div className="relative py-7 px-6">
                    <h3
                      className="text-[22px] font-bold text-center"
                      style={{ color: layer.color }}
                    >
                      {layer.label}
                    </h3>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Arrow indicator - DESKTOP ONLY */}
          <motion.div
            style={{ opacity: arrowOpacity, x: arrowX }}
            className="hidden lg:flex absolute -left-24 top-0 flex-col items-center gap-3"
          >
            <svg className="w-8 h-8 text-accent-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
            </svg>
            <p className="text-sm text-gray-400 font-medium -rotate-90 whitespace-nowrap origin-center translate-y-12">
              Higher Impact
            </p>
          </motion.div>

          {/* Arrow indicator - MOBILE STATIC */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.6 }}
            className="lg:hidden flex justify-center mt-6"
          >
            <div className="flex flex-col items-center gap-2">
              <svg className="w-6 h-6 text-accent-blue" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 10l7-7m0 0l7 7m-7-7v18" />
              </svg>
              <p className="text-[13px] text-gray-400 font-medium text-center">
                Higher Impact ↑
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default LeveragePyramid;